import React, {useEffect, useMemo, useState} from 'react';
import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import './ProductionsAndExportsDetails.scss';
import {t} from 'i18next';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../shared/types';
import ArrButton from '../../common/arr-button/ArrButton';
import {Add} from '@mui/icons-material';
import ArrMultiSelectDropdown from '../../common/arr-select-dropdown/ArrMultiSelectDropdown';
import ArrModal from '../../common/arr-modal/ArrModal';
import {ReactComponent as RadioActiveIcon} from '../../../assets/images/radio-active-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../assets/images/radio-enable-icon.svg';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  CreateProductionTemplatePayload,
  GetProductionTilesRequestParam,
  ICreateSetPayload,
  IGetProductionsResponse,
  IProductionSetItem,
  ProductionExportOptionsResponse,
  GetProductionSetInfoRequestParams,
  IProductionItem,
  CancelProductionRequest,
} from '../../../api/types/production.type';
import {
  useCreateProductionAndExportSet,
  useCreateProductionTemplate,
  useDeleteProduction,
  useDeleteProductionSet,
  useExports,
  useProductions,
  useProductionsAndExportsOptions,
  useGetProductionSetInfo,
  useUpdateProductionSet,
  useCancelProduction,
  useRetryCancelProduction,
} from '../../../hooks/productions/useProductions';
import {
  GetExportsTilesRequestParam,
  IGetExportsResponse,
} from '../../../api/types/export.type';
import DocumentTile from '../../common/arr-document-tile/ArrDocumentTile';
import {formatDateTime, isValidName} from '../../../utils/utils';
import {
  FilterOptions,
  SetDataOptions,
  getSortOptions,
  formatProductionSetTimestamp,
  stripSetTypeSuffix,
  formatProductionStatus,
} from './productionsAndExportsUtils';
import {RoleId, ToastType} from '../../../shared/constants';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  setIsFieldsChanged,
  setProduction,
  setProductionSetSetId,
} from '../../../redux/slice/productionSlice';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  saveTemplateSchema,
  SaveTemplateFormData,
} from '../../../schemas/productions/productionSchema';

interface ProductionsAndExportsDetailsProps {
  securityGroupId: number;
  projectId: number;
}

const ProductionsAndExportsDetails: React.FC<
  ProductionsAndExportsDetailsProps
> = ({securityGroupId, projectId}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginUser = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);

  const [productionData, setProductionData] =
    useState<ProductionExportOptionsResponse | null>(null);
  const [selectedProduction, setSelectedProduction] =
    useState<IProductionSetItem | null>(null);
  const [selectedFilters, setSelectedFilters] =
    useState<ISelectItem[]>(FilterOptions);
  const [selectedSort, setSelectedSort] = useState<ISelectItem | null>(null);
  const [showAddSetModal, setShowAddSetModal] = useState(false);
  const [selectedSet, setSelectedSet] = useState(SetDataOptions[0]);
  const [setNameValue, setSetNameValue] = useState('');
  const [productionAndExportTileData, setProductionAndExportTileData] =
    useState<IGetProductionsResponse | IGetExportsResponse | null>(null);
  const [createSetToast, setCreateSetToast] = useState<{
    show: boolean;
    type: ToastType;
    message: string;
  }>({show: false, type: ToastType.Success, message: ''});
  const [pageNumber, setPageNumber] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isSetNameValid, setIsSetNameValid] = useState<boolean>(true);
  const [setNameError, setSetNameError] = useState<string>('');
  const [editIsSetNameValid, setEditIsSetNameValid] = useState<boolean>(true);
  const [editSetNameError, setEditSetNameError] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productionToDelete, setProductionToDelete] = useState<string | null>(
    null
  );
  const [productionToCancel, setProductionToCancel] =
    useState<CancelProductionRequest | null>(null);
  const [deleteToast, setDeleteToast] = useState<{
    show: boolean;
    type: ToastType;
    message: string;
  }>({show: false, type: ToastType.Success, message: ''});
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showEditSetModal, setShowEditSetModal] = useState(false);
  const [productionToSaveAsTemplate, setProductionToSaveAsTemplate] = useState<
    number | null
  >(null);
  const [templateToast, setTemplateToast] = useState<{
    show: boolean;
    type: ToastType;
    message: string;
  }>({show: false, type: ToastType.Success, message: ''});
  const [cancelProductionToast, setCancelProductionToast] = useState<{
    show: boolean;
    type: ToastType;
    message: string;
  }>({show: false, type: ToastType.Success, message: ''});
  const [updateSetToast, setUpdateSetToast] = useState<{
    show: boolean;
    type: ToastType;
    message: string;
  }>({show: false, type: ToastType.Success, message: ''});
  const [editSetPayload, setEditSetPayload] = useState<{
    setName: string;
    isProductionSet: boolean;
  }>({setName: '', isProductionSet: true});
  const [pendingSelectedSetId, setPendingSelectedSetId] = useState<
    string | null
  >(null);
  const [modalSetName, setModalSetName] = useState<string>('');
  const [showDeleteSetModal, setShowDeleteSetModal] = useState(false);
  const [pendingSetToDelete, setPendingSetToDelete] =
    useState<IProductionSetItem | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const loadMoreRef = React.useRef<HTMLButtonElement | null>(null);

  const PAGE_SIZE = 12;

  const productionReduxData = useSelector((state: any) => state.production);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    reset,
    formState: {errors, isValid, isDirty},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setValue,
  } = useForm<SaveTemplateFormData>({
    resolver: zodResolver(saveTemplateSchema()),
    mode: 'onChange',
    defaultValues: {
      templateName: '',
    },
  });
  const {
    data: productionsAndExportsOptions,
    refetch: refetchProductionsAndExportsOptions,
  } = useProductionsAndExportsOptions(
    securityGroupId,
    Number.isFinite(securityGroupId) && securityGroupId > 0
  );

  const productionSetInfoParams = useMemo(() => {
    if (
      !selectedProduction ||
      !selectedProduction.value ||
      !Number.isFinite(securityGroupId) ||
      securityGroupId <= 0
    ) {
      return null;
    }

    const setId = Number(selectedProduction.value);
    if (!Number.isFinite(setId) || setId <= 0) {
      return null;
    }

    return {
      securityGrpId: securityGroupId,
      setId,
      selectedView: 'Tile',
    };
  }, [securityGroupId, selectedProduction]);

  const {data: productionSetInfo, isPending: isProductionSetInfoLoading} =
    useGetProductionSetInfo(
      (productionSetInfoParams ?? {
        securityGrpId: securityGroupId,
        setId: Number(selectedProduction?.value) || 0,
        selectedView: 'Tile',
      }) as GetProductionSetInfoRequestParams,
      Boolean(productionSetInfoParams)
    );

  const isProductionSetSelected = Boolean(
    selectedProduction?.htmlAttributes?.isProduction
  );

  const sortOptions = useMemo(
    () => getSortOptions(t, isProductionSetSelected),
    [t, isProductionSetSelected]
  );

  useEffect(() => {
    dispatch(setIsFieldsChanged({isFieldsChanged: false}));
  }, []);

  useEffect(() => {
    if (!sortOptions.length) {
      if (selectedSort !== null) {
        setSelectedSort(null);
      }
      return;
    }

    if (!selectedSort) {
      setSelectedSort(sortOptions[0]);
      return;
    }

    const matchingOption = sortOptions.find(
      (option) => option.key === selectedSort.key
    );

    if (!matchingOption) {
      setSelectedSort(sortOptions[0]);
      return;
    }

    if (
      matchingOption.value !== selectedSort.value ||
      matchingOption.label !== selectedSort.label
    ) {
      setSelectedSort(matchingOption);
    }
  }, [sortOptions, selectedSort]);

  const totalsTitle = isProductionSetSelected
    ? t('productionsAndExports.totalProductions')
    : t('productionsAndExports.totalExports');

  const newButtonLabel = isProductionSetSelected
    ? t('productionsAndExports.newProduction')
    : t('productionsAndExports.newExport');

  const isDefaultProductionSet =
    Boolean(selectedProduction?.htmlAttributes?.isDefault) ?? false;

  const getProgressState = (
    status?: string
  ): 'in-progress' | 'success' | 'failed' | 'completed' | 'draft' => {
    const normalized = (status || '').toLowerCase();
    if (normalized.includes('progress')) {
      return 'in-progress';
    }
    if (normalized.includes('fail')) {
      return 'failed';
    }
    if (normalized.includes('success')) {
      return 'success';
    }
    if (normalized.includes('complete')) {
      return 'completed';
    }
    return 'draft';
  };

  const clampPercent = (value?: number | null) => {
    if (value === null || value === undefined) {
      return 0;
    }
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return 0;
    }
    return Math.max(0, Math.min(100, Math.round(numeric)));
  };

  const createdInfoLabel = useMemo(() => {
    if (isProductionSetInfoLoading) {
      return t('common.loading');
    }
    if (!productionSetInfo) {
      return '';
    }

    const createdBy =
      productionSetInfo.createdBy?.trim() || t('common.notAvailable');
    const createdOn =
      formatProductionSetTimestamp(productionSetInfo.createdOn) ||
      t('common.notAvailable');

    return t('productionsAndExports.createdInfoLabel', {
      createdBy,
      createdOn,
    });
  }, [isProductionSetInfoLoading, productionSetInfo, t]);

  const {data: productionsTileData, refetch: refetchProductions} =
    useProductions(
      {
        securityGrpId: securityGroupId,
        setId: Number(selectedProduction?.value),
        Type: 'Tile',
        filterBy: selectedFilters.map((item) => item.key).join(','),
        sortBy: selectedSort?.key,
        pageSize: PAGE_SIZE,
        pageNumber: pageNumber,
      } as GetProductionTilesRequestParam,
      Boolean(selectedProduction) &&
        Number.isFinite(securityGroupId) &&
        securityGroupId > 0 &&
        Boolean(selectedProduction?.htmlAttributes.isProduction)
    );

  const {data: exportsTileData, refetch: refetchExports} = useExports(
    {
      securityGrpId: securityGroupId,
      setId: Number(selectedProduction?.value),
      Type: 'Tile',
      filterBy: selectedFilters.map((item) => item.key).join(','),
      sortBy: selectedSort?.key,
      pageSize: PAGE_SIZE,
      pageNumber: pageNumber,
    } as GetExportsTilesRequestParam,
    Boolean(selectedProduction) &&
      Number.isFinite(securityGroupId) &&
      securityGroupId > 0 &&
      Boolean(!selectedProduction?.htmlAttributes.isProduction)
  );

  const {mutate: createProductionAndExportSet} =
    useCreateProductionAndExportSet();

  const {
    mutate: deleteProduction,
    isPending: isDeleting,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    error: deleteError,
  } = useDeleteProduction(
    (data) => {
      // Success callback - use server response message directly
      setDeleteToast({
        show: true,
        type: ToastType.Success,
        message: data.message,
      });
      setShowDeleteModal(false);
      setProductionToDelete(null);

      // Reset pagination and clear existing data before refetch
      setPageNumber(1);
      setProductionAndExportTileData(null);

      // Refresh the tile list data
      if (selectedProduction?.htmlAttributes.isProduction) {
        refetchProductions();
      } else {
        refetchExports();
      }
    },
    (error) => {
      // Error callback - use server error message or fallback
      setDeleteToast({
        show: true,
        type: ToastType.Error,
        message: error.message || t('common.error'),
      });
    }
  );

  const {mutate: cancelProduction, isPending: isCanelling} =
    useCancelProduction(
      () => {
        // Success callback - use server response message directly
        setCancelProductionToast({
          show: true,
          type: ToastType.Success,
          message: t('cancelProduction.toast.successMessage'),
        });
        // Refresh the tile list data
        setPageNumber(1);
        setProductionAndExportTileData(null);
        if (selectedProduction?.htmlAttributes.isProduction) {
          refetchProductions();
        } else {
          refetchExports();
        }
      },
      (error) => {
        // Error callback - use server error message or fallback
        setCancelProductionToast({
          show: true,
          type: ToastType.Error,
          message: error.message || t('common.error'),
        });
      }
    );

  const {mutate: retryCancelProduction, isPending: isRetrying} =
    useRetryCancelProduction(
      () => {
        setCancelProductionToast({
          show: true,
          type: ToastType.Success,
          message: t('cancelProduction.toast.successMessage'),
        });
        setPageNumber(1);
        setProductionAndExportTileData(null);
        if (selectedProduction?.htmlAttributes.isProduction) {
          refetchProductions();
        } else {
          refetchExports();
        }
      },
      (error) => {
        setCancelProductionToast({
          show: true,
          type: ToastType.Error,
          message: error.message || t('common.error'),
        });
      }
    );

  const {mutate: deleteProductionSet, isPending: isDeletingSet} =
    useDeleteProductionSet(
      (data) => {
        setDeleteToast({
          show: true,
          type: ToastType.Success,
          message: data.message || t('common.success'),
        });

        const deletedSetValue = pendingSetToDelete?.value ?? null;

        setShowDeleteSetModal(false);
        setPendingSetToDelete(null);
        setPendingSelectedSetId(null);
        setSelectedProduction(null);
        setModalSetName('');
        setPageNumber(1);
        setProductionAndExportTileData(null);

        if (deletedSetValue) {
          setProductionData((prev) => {
            if (!prev?.productionSets?.length) {
              return prev;
            }

            return {
              ...prev,
              productionSets: prev.productionSets.filter(
                (set) => set.value !== deletedSetValue
              ),
            };
          });
        }

        refetchProductionsAndExportsOptions().then((result) => {
          const optionsData =
            (result?.data as ProductionExportOptionsResponse | undefined) ??
            productionsAndExportsOptions ??
            null;

          if (!optionsData?.productionSets?.length) {
            setProductionData(optionsData);
            return;
          }

          setProductionData(optionsData);

          const fallbackSet =
            optionsData.productionSets.find((set) => set.isSelected) ??
            optionsData.productionSets[0];

          if (!fallbackSet) {
            setSelectedProduction(null);
            setPendingSelectedSetId(null);
            return;
          }

          setSelectedProduction(fallbackSet);
          setModalSetName(stripSetTypeSuffix(fallbackSet.text ?? ''));
          dispatch(
            setProduction({
              securityGroupId: securityGroupId,
              productionSetId: Number(fallbackSet.value),
            })
          );

          if (fallbackSet.htmlAttributes?.isProduction) {
            refetchProductions();
          } else {
            refetchExports();
          }
        });
      },
      (error) => {
        setDeleteToast({
          show: true,
          type: ToastType.Error,
          message: error.message || t('common.error'),
        });
        setShowDeleteSetModal(false);
        setPendingSetToDelete(null);
        setPendingSelectedSetId(null);
      }
    );

  const {mutate: createProductionTemplate, isPending: isCreatingTemplate} =
    useCreateProductionTemplate(
      () => {
        // Success callback
        setTemplateToast({
          show: true,
          type: ToastType.Success,
          message: t(
            'productionsAndExports.saveAsTemplateModal.successMessage'
          ),
        });
        setShowSaveTemplateModal(false);
        reset();
        setProductionToSaveAsTemplate(null);
      },
      (error) => {
        // Error callback
        setTemplateToast({
          show: true,
          type: ToastType.Error,
          message: error.message || 'Failed to create template',
        });
      }
    );

  const {mutate: updateProductionSet, isPending: isUpdatingSet} =
    useUpdateProductionSet(
      (data) => {
        setShowEditSetModal(false);
        setUpdateSetToast({
          show: true,
          type: ToastType.Success,
          message: data.message || t('common.success'),
        });
        if (selectedProduction) {
          const updatedId = selectedProduction.value ?? null;
          setPendingSelectedSetId(updatedId);
          setModalSetName(stripSetTypeSuffix(editSetPayload.setName ?? ''));
        }
        setEditSetPayload({setName: '', isProductionSet: true});
        setProductionData((prev) => {
          if (!prev?.productionSets?.length || !selectedProduction) {
            return prev;
          }

          const updatedSets = prev.productionSets.map((set) =>
            set.value === selectedProduction.value
              ? {
                  ...set,
                  text: (() => {
                    const setTypeLabel = editSetPayload.isProductionSet
                      ? t('productionsAndExports.deleteSet.setTypes.production')
                      : t('productionsAndExports.deleteSet.setTypes.export');
                    return `${editSetPayload.setName} (${setTypeLabel})`;
                  })(),
                  htmlAttributes: {
                    ...set.htmlAttributes,
                    isProduction: editSetPayload.isProductionSet ? 1 : 0,
                  },
                }
              : set
          );

          const updatedSelection = selectProductionById(
            updatedSets,
            selectedProduction.value
          );

          if (updatedSelection) {
            setSelectedProduction(updatedSelection);
            setModalSetName(stripSetTypeSuffix(updatedSelection.text ?? ''));
            dispatch(
              setProduction({
                securityGroupId: securityGroupId,
                productionSetId: Number(updatedSelection.value),
              })
            );
          }

          return {
            ...prev,
            productionSets: updatedSets,
          };
        });

        setPageNumber(1);
        setProductionAndExportTileData(null);
        if (editSetPayload.isProductionSet) {
          refetchProductions();
        } else {
          refetchExports();
        }

        setPendingSelectedSetId(null);
      },
      (error) => {
        setShowEditSetModal(false);
        setEditSetPayload({setName: '', isProductionSet: true});
        setModalSetName('');
        setUpdateSetToast({
          show: true,
          type: ToastType.Error,
          message: error.message || t('common.error'),
        });
      }
    );

  const loadNextPage = () => {
    if (!isFetchingMore) {
      setIsFetchingMore(true);
      setPageNumber((prev) => prev + 1);
    }
  };

  // Use IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            productionAndExportTileData?.productionCount &&
            pageNumber * PAGE_SIZE <
              (productionAndExportTileData.productionCount ?? 0)
          ) {
            loadNextPage();
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: '0px',
        threshold: 0.5,
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [loadMoreRef, productionAndExportTileData, pageNumber]);

  useEffect(() => {
    if (
      productionsTileData &&
      selectedProduction?.htmlAttributes?.isProduction
    ) {
      setProductionAndExportTileData((prev) => {
        if (!prev || pageNumber === 1) return productionsTileData;
        return {
          ...productionsTileData,
          productionItemDetails: [
            ...(prev.productionItemDetails ?? []),
            ...(productionsTileData.productionItemDetails ?? []),
          ],
        };
      });
    } else if (
      exportsTileData &&
      !selectedProduction?.htmlAttributes?.isProduction
    ) {
      setProductionAndExportTileData((prev) => {
        if (!prev || pageNumber === 1) return exportsTileData;
        return {
          ...exportsTileData,
          productionItemDetails: [
            ...(prev.productionItemDetails ?? []),
            ...(exportsTileData.productionItemDetails ?? []),
          ],
        };
      });
    }
  }, [productionsTileData, exportsTileData, pageNumber, selectedProduction]);

  useEffect(() => {
    if (isFetchingMore) {
      setIsFetchingMore(false);
    }
  }, [productionsTileData, exportsTileData]);

  // Stop loading if all data fetched
  useEffect(() => {
    const total = productionAndExportTileData?.productionCount ?? 0;
    const loaded =
      productionAndExportTileData?.productionItemDetails?.length ?? 0;

    if (loaded >= total) {
      setIsFetchingMore(false);
    }
  }, [productionAndExportTileData]);

  // notify-all SignalR events to refresh tiles on Production Home.
  useEffect(() => {
    if (lastEvent?.payload?.type !== 'ProductionCancelPayload') return;

    const message = lastEvent?.payload?.payload;
    if (!message) return;

    const isPA = String(loginUser?.roleId) === RoleId.ProjectAdministrator;
    const isRM = String(loginUser?.roleId) === RoleId.ReviewManager;

    let shouldRefresh = false;

    if (isPA) {
      shouldRefresh =
        typeof loginUser?.projectId === 'number' &&
        typeof message.projectId === 'number' &&
        loginUser.projectId === message.projectId;
    } else if (isRM) {
      shouldRefresh =
        typeof loginUser?.projectId === 'number' &&
        typeof message.projectId === 'number' &&
        loginUser.projectId === message.projectId &&
        typeof loginUser?.securityGroupId === 'number' &&
        typeof message.securityGroupId === 'number' &&
        loginUser.securityGroupId === message.securityGroupId;
    }

    if (!shouldRefresh) return;

    setPageNumber(1);
    setProductionAndExportTileData(null);

    if (selectedProduction?.htmlAttributes?.isProduction) {
      refetchProductions();
    } else {
      refetchExports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEvent]);

  const selectProductionById = React.useCallback(
    (
      sets: IProductionSetItem[] | undefined,
      id: string | null | undefined
    ): IProductionSetItem | null => {
      if (!sets?.length || !id) {
        return null;
      }

      return sets.find((item) => item.value === id) ?? null;
    },
    []
  );

  const isCancelDisabled = (item: IProductionItem): boolean => {
    if (item?.canCancel === false) {
      return true;
    }
    return false;
  };

  const triggerCancel = (isRetry = false) => {
    setShowConfirm(false);
    if (productionToCancel) {
      const requestData: CancelProductionRequest = {
        moduleType: productionToCancel.moduleType,
        encryptedProductionId: productionToCancel.encryptedProductionId,
        productionName: productionToCancel.productionName,
      };
      if (isRetry) {
        retryCancelProduction(requestData);
      } else {
        cancelProduction(requestData);
      }
    }
  };

  useEffect(() => {
    if (productionsAndExportsOptions?.productionSets?.length) {
      const sets = productionsAndExportsOptions.productionSets;

      const nextSelected =
        selectProductionById(sets, pendingSelectedSetId) ??
        sets.find((item) => item.isSelected) ??
        null;

      setPageNumber(1);
      setSelectedProduction(nextSelected);
      setProductionData({
        ...productionsAndExportsOptions,
        productionSets: sets,
      });

      if (nextSelected) {
        dispatch(
          setProduction({
            securityGroupId: securityGroupId,
            productionSetId: Number(nextSelected.value),
          })
        );
      }
    }
  }, [
    productionsAndExportsOptions,
    securityGroupId,
    dispatch,
    pendingSelectedSetId,
    selectProductionById,
  ]);

  const handleAddSet = () => {
    setShowAddSetModal(true);
  };
  const handleEditSet = () => {
    if (!selectedProduction) {
      return;
    }

    const strippedName = stripSetTypeSuffix(selectedProduction.text ?? '');
    setEditSetPayload({
      setName: strippedName,
      isProductionSet: Boolean(selectedProduction.htmlAttributes?.isProduction),
    });
    setModalSetName(strippedName);
    setEditIsSetNameValid(true);
    setEditSetNameError('');
    setShowEditSetModal(true);
  };

  const handleDeleteSetClick = () => {
    if (!selectedProduction) {
      return;
    }

    setPendingSetToDelete(selectedProduction);
    setShowDeleteSetModal(true);
  };

  const handleCloseDeleteSetModal = () => {
    setShowDeleteSetModal(false);
    setPendingSetToDelete(null);
  };

  const handleConfirmDeleteSet = () => {
    if (!pendingSetToDelete) {
      return;
    }

    const hasValidProject = Number.isFinite(projectId) && projectId > 0;
    if (!hasValidProject) {
      setDeleteToast({
        show: true,
        type: ToastType.Error,
        message: t('productionsAndExports.deleteSet.missingProject'),
      });
      return;
    }

    deleteProductionSet({
      SecurityGrpId: securityGroupId,
      ProductionSetId: Number(pendingSetToDelete.value),
      SelectedView: 'Tile',
      ProjectId: projectId,
      IsProductionSet: Boolean(pendingSetToDelete.htmlAttributes?.isProduction),
    });
  };

  const handleClearSet = () => {
    setSetNameValue('');
    setShowAddSetModal(false);
    setSetNameError('');
    setIsSetNameValid(true);
    setSelectedSet(SetDataOptions[0]);
  };

  const handleCreateSet = () => {
    if (!setNameValue?.trim()) {
      setIsSetNameValid(false);
      if (selectedSet)
        setSetNameError(
          `${selectedSet.label ?? ''} ${t('productionsAndExports.nameError')}`
        );
      return;
    }
    if (!isValidName(setNameValue?.trim())) {
      setIsSetNameValid(false);
      setSetNameError(t('productionsAndExports.invalidCharError'));
      return;
    }
    const payload: ICreateSetPayload = {
      setName: setNameValue,
      isProductionSet: selectedSet.value === SetDataOptions[0].value,
      securityGroupId: securityGroupId,
    };

    createProductionAndExportSet(payload, {
      onSuccess: (data) => {
        setCreateSetToast({
          show: true,
          type: ToastType.Success,
          message: data?.message || t('common.success'),
        });
        handleClearSet();
        refetchProductionsAndExportsOptions();
      },
      onError: (error: any) => {
        setCreateSetToast({
          show: true,
          type: ToastType.Error,
          message: error?.message || t('common.error'),
        });
        handleClearSet();
      },
    });
  };

  const handleNewProduction = () => {
    if (!selectedProduction) {
      return;
    }

    navigate('/Production/Home/New', {
      state: {
        securityGroupId: securityGroupId,
        productionSetId: Number(selectedProduction.value),
        isProductionSet: Boolean(
          selectedProduction.htmlAttributes?.isProduction
        ),
      },
    });
  };

  const handleRefresh = async () => {
    setPageNumber(1);

    if (selectedProduction?.htmlAttributes?.isProduction) {
      const result = await refetchProductions();
      setProductionAndExportTileData(result.data ?? null);
    } else {
      const result = await refetchExports();
      setProductionAndExportTileData(result.data ?? null);
    }
  };

  const onProductionChange = (val: IProductionSetItem | null) => {
    setPageNumber(1);
    setSelectedProduction(val);
    if (!val) {
      setSelectedProduction(null);
      return;
    }

    setSelectedProduction(val);
    dispatch(setProductionSetSetId(Number(val.value)));
  };

  const onFiltersChange = (vals: ISelectItem[]) => {
    setPageNumber(1);
    setSelectedFilters(vals);
  };

  const onSortChange = (val: ISelectItem | null) => {
    setPageNumber(1);
    setSelectedSort(val);
  };

  const handleSaveTemplate = (data: SaveTemplateFormData) => {
    if (!productionToSaveAsTemplate) {
      return;
    }

    const payload: CreateProductionTemplatePayload = {
      field: data.templateName.trim(),
      productionId: productionToSaveAsTemplate,
      productionSetId: productionReduxData.productionSetId,
      securityGrpId: securityGroupId,
    };

    createProductionTemplate(payload);
  };

  return (
    <Box className={'productions-details-container'}>
      <Box className="select-production">
        <Typography
          className="title-text"
          data-testid={`productions-and-exports-details-select-production`}
        >
          {t('productionsAndExports.selectProduction')}
        </Typography>
        <Box className="select-production-dropdown">
          <ArrSingleSelectDropdown
            dropdownLabel={t('productionsAndExports.selectProduction')}
            dropdownItems={
              (productionData?.productionSets as unknown as ISelectItem[]) ?? []
            }
            selectedValues={
              (selectedProduction as unknown as ISelectItem | null) ?? null
            }
            dropdownItemKey={'value'}
            dropdownItemDisplay={'text'}
            sizeVariant="medium"
            onSelectionChange={(item) =>
              onProductionChange(
                (item as unknown as IProductionSetItem) ?? null
              )
            }
            dataTestId="asd-prod-and-export-select-prod"
            customWidth={25}
            toolTip={false}
            keyPressActive={true}
            searchable
            showNoResultsText
          />
          {!isDefaultProductionSet && (
            <Box className="select-production-actions">
              <ArrIconButton
                ariaLabel={t(
                  'productionsAndExports.actions.editProductionSet',
                  'Edit Production/Export Set'
                )}
                tooltipContent={t(
                  'productionsAndExports.actions.editProductionSet',
                  'Edit Production/Export Set'
                )}
                icon={<EditOutlinedIcon />}
                disabled={!selectedProduction}
                size="small"
                data-testid="btn-edit-set"
                onClick={handleEditSet}
                testId="productions-and-exports-details-edit-production-set-icon-button"
              />
              <ArrIconButton
                ariaLabel={t(
                  'productionsAndExports.actions.deleteProductionSet',
                  'Delete Production/Export Set'
                )}
                tooltipContent={t(
                  'productionsAndExports.actions.deleteProductionSet',
                  'Delete Production/Export Set'
                )}
                icon={<DeleteOutlineIcon />}
                disabled={!selectedProduction}
                size="small"
                data-testid="btn-delete-set"
                onClick={handleDeleteSetClick}
                testId="productions-and-exports-details-delete-production-set-icon-button"
              />
            </Box>
          )}
          <ArrButton
            variant="primary"
            size="medium"
            aria-label={t('productionsAndExports.createNewSet')}
            className="custom-button"
            startIcon={<Add />}
            onClick={handleAddSet}
            dataTestId="btn-create-new-set"
          >
            {t('productionsAndExports.createNewSet')}
          </ArrButton>
        </Box>
        {createdInfoLabel && (
          <Box className="select-production__meta">
            <Typography
              className="select-production__meta-text"
              data-testid={`productions-and-exports-details-created-label`}
            >
              {createdInfoLabel}
            </Typography>
          </Box>
        )}
      </Box>
      <Box className="productions-details">
        <Box className="productions-add-container">
          <Box className="productions-total">
            <Typography
              className="total-text"
              data-testid={`productions-and-exports-details-totals`}
            >
              {totalsTitle}
              <span
                className="total-sub-text"
                data-testid={`productions-and-exports-details-production-count`}
              >{`(${productionAndExportTileData?.productionCount ?? 0})`}</span>
            </Typography>
            <Typography
              className="total-docs"
              data-testid={`productions-and-exports-details-total-document`}
            >
              {t('productionsAndExports.totalDocument', {
                totalDocs:
                  productionAndExportTileData?.totalNumberDocuments ?? 0,
              })}
            </Typography>
          </Box>
          <Box className="add-production-buttons">
            <ArrButton
              variant="primary"
              size="medium"
              aria-label={newButtonLabel}
              className="custom-button"
              startIcon={<Add />}
              onClick={handleNewProduction}
              dataTestId="btn-new-production"
            >
              {newButtonLabel}
            </ArrButton>
            <ArrButton
              variant="secondary"
              size="medium"
              aria-label={t('productionsAndExports.refresh')}
              onClick={handleRefresh}
              dataTestId="btn-refresh"
            >
              {t('productionsAndExports.refresh')}
            </ArrButton>
          </Box>
        </Box>
        <Box className="productions-filters">
          <ArrMultiSelectDropdown
            dropdownLabel={t('productionsAndExports.filterBy')}
            dropdownItems={FilterOptions}
            selectedValues={selectedFilters}
            dropdownItemKey={'key'}
            dropdownItemDisplay={'value'}
            onSelectionChange={onFiltersChange}
            dataTestId="select-prod"
            checkboxDataTestId="productions-and-exports-filter-dropdown-checkbox"
            customWidth={17.5}
            multiple
            withSelectAll={false}
            customDisplayLabel={
              selectedFilters.length > 1
                ? t('productionsAndExports.moreThan')
                : undefined
            }
          />
          <ArrSingleSelectDropdown
            dropdownLabel={t('productionsAndExports.sortBy')}
            dropdownItems={sortOptions}
            selectedValues={selectedSort}
            dropdownItemKey={'key'}
            dropdownItemDisplay={'value'}
            onSelectionChange={onSortChange}
            dataTestId="asd-select-prod-sortBy"
            customWidth={17.5}
          />
        </Box>
        <Box className="productions-card-container">
          {productionAndExportTileData?.productionItemDetails?.length &&
          productionAndExportTileData?.productionItemDetails?.length > 0 ? (
            <Grid container spacing={1}>
              {productionAndExportTileData?.productionItemDetails?.map(
                (item, index) => {
                  const completionPercent = clampPercent(
                    item.completionPercentage
                  );
                  const progressState = getProgressState(item.status);
                  return (
                    <Grid
                      data-testid="tile-item"
                      key={item.productionId}
                      size={{xs: 12, sm: 6, md: 4, lg: 3}}
                    >
                      <DocumentTile
                        dataTestId={`p-export-${index}`}
                        primaryLabel={item.productionName}
                        tertiaryLabel={formatProductionStatus(
                          item.productionUIStatus,
                          item.status,
                          item.productionStateCode
                        )}
                        primaryLabelColor="text-action-default"
                        lastModifiedBy={item.lastModifiedBy}
                        lastModifiedTimestamp={formatDateTime(
                          item.lastModifiedOn
                        )}
                        statusTimestamp={`${item.status} ${formatDateTime(item.lastModifiedOn)}`}
                        metaDataList={[
                          {
                            label: 'Documents',
                            value: item.produceDoc?.toLocaleString() ?? '0',
                          },
                        ]}
                        bates={{
                          startNumber: item.startingBates,
                          endNumber: item.endingBates,
                        }}
                        progressBarProps={{
                          height: 24,
                          percent: completionPercent,
                          state: progressState,
                        }}
                        actionMenuItems={[
                          {
                            id: '1',
                            label: t('productionsAndExports.openWizard'),
                            onClick: () =>
                              navigate(
                                `/Production/Home/Edit?productionId=${item.encryptedProductionId}`
                              ),
                          },
                          {
                            id: 'cancel-production',
                            label: (() => {
                              const uiStatus =
                                item?.productionUIStatus?.toUpperCase();

                              const isRetryCancel =
                                uiStatus === 'CANCEL FAILED';

                              return isRetryCancel
                                ? t('cancelProduction.retryCancel')
                                : t('common.cancel');
                            })(),

                            onClick: () => {
                              const uiStatus =
                                item?.productionUIStatus?.toUpperCase();

                              const cancelData: CancelProductionRequest = {
                                moduleType: selectedProduction?.htmlAttributes
                                  ?.isProduction
                                  ? 'PRODUCTION'
                                  : 'EXPORT',
                                encryptedProductionId:
                                  item.encryptedProductionId,
                                productionName: item.productionName,
                              };

                              setProductionToCancel(cancelData);

                              if (uiStatus === 'CANCEL FAILED') {
                                retryCancelProduction(cancelData);
                              } else {
                                setShowConfirm(true);
                              }
                            },

                            disabled: (() => {
                              const uiStatus =
                                item?.productionUIStatus?.toUpperCase();
                              const isCancelFailed =
                                uiStatus === 'CANCEL FAILED';

                              return (
                                (!isCancelFailed && isCancelDisabled(item)) ||
                                isCanelling ||
                                isRetrying
                              );
                            })(),
                          },
                          {
                            id: 'delete-production',
                            label: t('productionsAndExports.delete'),
                            onClick: () => {
                              setProductionToDelete(item.encryptedProductionId);
                              setShowDeleteModal(true);
                            },
                            disabled:
                              item.status === 'COMPLETED' ||
                              item.status === 'INPROGRESS',
                          },
                          {
                            id: 'save-as-template',
                            label: t('productionsAndExports.saveAsTemplate'),
                            onClick: () => {
                              setProductionToSaveAsTemplate(item.productionId);
                              setShowSaveTemplateModal(true);
                            },
                          },
                        ]}
                        menuDirection="bottom-right"
                      />
                    </Grid>
                  );
                }
              )}
            </Grid>
          ) : (
            <Box
              data-testid="empty-container"
              className="productions-empty-container"
            >
              <Typography
                className="empty-text"
                data-testid={`productions-and-exports-details-typography-btn`}
              >
                {isProductionSetSelected
                  ? t('productionsAndExports.emptyProductions')
                  : t('productionsAndExports.emptyExports')}
              </Typography>
            </Box>
          )}
          {(productionAndExportTileData?.productionCount ?? 0) > 0 &&
            (productionAndExportTileData?.productionCount ?? 0) !==
              productionAndExportTileData?.productionItemDetails?.length && (
              <Box ref={loadMoreRef}>
                <ArrButton
                  variant="secondary"
                  size="medium"
                  aria-label={t('productionsAndExports.loadMore')}
                  onClick={() => {}}
                  className="loadMore"
                  dataTestId="btn-load-more"
                >
                  {t('productionsAndExports.loadMore')}
                </ArrButton>
              </Box>
            )}
        </Box>
      </Box>
      <ArrModal
        showModal={showAddSetModal}
        showClose={false}
        onClose={() => handleClearSet()}
        title={t('productionsAndExports.addSet.title')}
        dataTestId="productionsandexports-modal"
        buttons={[
          {
            id: '1',
            label: t('common.cancel'),
            onClick: () => handleClearSet(),
            variant: 'secondary',
            dataTestId: 'paed-cancel-btn',
          },
          {
            id: '2',
            label: t('common.save'),
            onClick: () => handleCreateSet(),
            variant: 'primary',
            dataTestId: 'paed-save-btn',
          },
        ]}
      >
        <Box className="productions-details-container__set-modal-container">
          <RadioGroup
            row
            value={selectedSet.value}
            onChange={(e) => {
              const selectedObj = SetDataOptions.find(
                (opt) => opt.value === e.target.value
              );
              if (selectedObj) setSelectedSet(selectedObj);
            }}
            className="custom-radio-group"
          >
            {SetDataOptions.map(({value, label}, index) => {
              return (
                <FormControlLabel
                  key={value}
                  value={value}
                  control={
                    <Radio
                      slotProps={{
                        input: {
                          'aria-label': String(value),
                          'data-testid': `radio-${value}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>,
                      }}
                      icon={<RadioEnableIcon />}
                      checkedIcon={<RadioActiveIcon />}
                      className="custom-radio-label"
                      data-testid={`data-options-${index}`}
                    />
                  }
                  className="custom-radio-label"
                  label={
                    <span
                      className="radio-text"
                      data-testid={`productions-and-exports-details-label-${index}`}
                    >
                      {label}
                    </span>
                  }
                />
              );
            })}
          </RadioGroup>
          <ArrTextField
            id="set-name"
            dataTestId="set-name-text-input"
            state={isSetNameValid ? 'enabled' : 'error'}
            label={t('productionsAndExports.addSet.setName')}
            placeholder={t('productionsAndExports.addSet.setNamePlaceHolder')}
            value={setNameValue}
            onChange={(e) => {
              const val = e.target.value;
              setSetNameValue(val);
              if (val === '' || isValidName(val)) {
                setIsSetNameValid(true);
                setSetNameError('');
              } else {
                setIsSetNameValid(false);
                setSetNameError(t('productionsAndExports.invalidCharError'));
              }
            }}
            supportText={!isSetNameValid ? setNameError : ''}
          />
        </Box>
      </ArrModal>
      {showDeleteSetModal && pendingSetToDelete && (
        <ArrModal
          showModal={showDeleteSetModal}
          showClose={false}
          onClose={handleCloseDeleteSetModal}
          title={t('common.delete')}
          dataTestId="delete-pending-modal"
          buttons={[
            {
              id: 'cancel-delete-set',
              label: t('common.cancel'),
              onClick: handleCloseDeleteSetModal,
              variant: 'secondary',
              dataTestId: 'dpm-cancel-btn',
            },
            {
              id: 'confirm-delete-set',
              label: t('common.delete'),
              onClick: handleConfirmDeleteSet,
              variant: 'danger',
              disabled: isDeletingSet,
              dataTestId: 'dpm-delete-btn',
            },
          ]}
        >
          <Typography
            className="delete-set-modal__message"
            data-testid={`productions-and-exports-details-message`}
          >
            {t('productionsAndExports.deleteSet.message', {
              setType: pendingSetToDelete.htmlAttributes?.isProduction
                ? t(
                    'productionsAndExports.deleteSet.setTypes.production',
                    'Production Set'
                  )
                : t(
                    'productionsAndExports.deleteSet.setTypes.export',
                    'Export Set'
                  ),
            })}
          </Typography>
        </ArrModal>
      )}
      {showEditSetModal && (
        <ArrModal
          showModal={showEditSetModal}
          showClose={false}
          onClose={() => setShowEditSetModal(false)}
          title={t('productionsAndExports.editSet.title', 'Update Set')}
          dataTestId="showeditset-modal"
          buttons={[
            {
              id: 'cancel-edit-set',
              label: t('common.cancel'),
              onClick: () => setShowEditSetModal(false),
              variant: 'secondary',
              dataTestId: 'ses-canel-btn',
            },
            {
              id: 'confirm-edit-set',
              label: t('common.save'),
              dataTestId: 'ses-save-btn',
              onClick: () => {
                if (!selectedProduction) {
                  return;
                }

                const trimmedName = editSetPayload.setName.trim();
                if (!trimmedName) {
                  setEditIsSetNameValid(false);
                  setEditSetNameError(
                    `${t('productionsAndExports.addSet.setName')} ${t(
                      'productionsAndExports.nameError'
                    )}`
                  );
                  return;
                }

                if (!isValidName(trimmedName)) {
                  setEditIsSetNameValid(false);
                  setEditSetNameError(
                    t('productionsAndExports.invalidCharError')
                  );
                  return;
                }

                updateProductionSet({
                  field: trimmedName,
                  isProductionSet: editSetPayload.isProductionSet,
                  securityGroupId: securityGroupId,
                  productionSetId: Number(selectedProduction.value),
                });
              },
              variant: 'primary',
              disabled: isUpdatingSet,
            },
          ]}
        >
          <Box className="productions-details-container__set-modal-container">
            <RadioGroup
              row
              value={
                editSetPayload.isProductionSet
                  ? SetDataOptions[0].value
                  : SetDataOptions[1].value
              }
              onChange={(e) => {
                const isProductionSetValue =
                  e.target.value === SetDataOptions[0].value;
                setEditSetPayload((prev) => ({
                  ...prev,
                  isProductionSet: isProductionSetValue,
                }));
              }}
              className="custom-radio-group"
            >
              {SetDataOptions.map(({value, label}, index) => {
                const isProductionOption = value === SetDataOptions[0].value;
                const isDisabled =
                  (selectedProduction?.htmlAttributes?.isProduction &&
                    !isProductionOption) ||
                  (!selectedProduction?.htmlAttributes?.isProduction &&
                    isProductionOption);

                return (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={
                      <Radio
                        slotProps={{
                          input: {
                            'aria-label': String(value),
                            'data-testid': `radio-${value}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>,
                        }}
                        icon={<RadioEnableIcon />}
                        checkedIcon={<RadioActiveIcon />}
                        className="custom-radio-label"
                        disabled={isDisabled}
                        data-testid={`productions-and-exports-details-radio-option-${index}`}
                      />
                    }
                    className="custom-radio-label"
                    label={
                      <span
                        className="radio-text"
                        data-testid={`productions-and-exports-details-data-option-${index}`}
                      >
                        {label}
                      </span>
                    }
                  />
                );
              })}
            </RadioGroup>
            <ArrTextField
              id="edit-set-name"
              dataTestId="edit-set-name-text-input"
              state={editIsSetNameValid ? 'enabled' : 'error'}
              label={t('productionsAndExports.addSet.setName')}
              placeholder={t('productionsAndExports.addSet.setNamePlaceHolder')}
              value={modalSetName}
              onChange={(e) => {
                const value = e.target.value;
                setModalSetName(value);
                setEditSetPayload((prev) => ({
                  ...prev,
                  setName: value,
                }));

                if (value === '' || isValidName(value)) {
                  setEditIsSetNameValid(true);
                  setEditSetNameError('');
                } else {
                  setEditIsSetNameValid(false);
                  setEditSetNameError(
                    t('productionsAndExports.invalidCharError')
                  );
                }
              }}
              supportText={!editIsSetNameValid ? editSetNameError : ''}
            />
          </Box>
        </ArrModal>
      )}
      {showDeleteModal && (
        <ArrModal
          showModal={showDeleteModal}
          showClose={false}
          onClose={() => {
            setShowDeleteModal(false);
            setProductionToDelete(null);
          }}
          title={t('common.delete')}
          dataTestId="show-delete-modal-1"
          buttons={[
            {
              id: 'cancel-delete',
              label: t('common.cancel'),
              onClick: () => {
                setShowDeleteModal(false);
                setProductionToDelete(null);
              },
              variant: 'secondary',
              dataTestId: 'sdm1-cancel-btn',
            },
            {
              id: 'confirm-delete',
              label: t('common.delete'),
              onClick: () => {
                if (productionToDelete) {
                  deleteProduction(productionToDelete);
                }
              },
              variant: 'danger',
              disabled: isDeleting,
              dataTestId: 'sdm1-delete-btn',
            },
          ]}
        >
          <Typography
            className="delete-set-modal__message"
            data-testid={`productions-and-exports-details-title`}
          >
            {t('productionsAndExports.deleteProduction.title')}
          </Typography>
        </ArrModal>
      )}
      {showSaveTemplateModal && (
        <ArrModal
          showModal={showSaveTemplateModal}
          showClose={false}
          onClose={() => {
            setShowSaveTemplateModal(false);
            reset();
            setProductionToSaveAsTemplate(null);
          }}
          title={t('productionsAndExports.saveAsTemplateModal.title')}
          dataTestId="save-template-modal"
          buttons={[
            {
              id: 'cancel-save-template',
              label: t('common.cancel'),
              onClick: () => {
                setShowSaveTemplateModal(false);
                reset();
                setProductionToSaveAsTemplate(null);
              },
              variant: 'secondary',
              dataTestId: 'stm-cancel-btn',
            },
            {
              id: 'confirm-save-template',
              label: t('common.save'),
              onClick: handleSubmit(handleSaveTemplate),
              variant: 'primary',
              disabled: isCreatingTemplate || !isValid || !isDirty,
              dataTestId: 'stm-save-btn',
            },
          ]}
        >
          <Box className="productions-details-container__template-modal-container">
            <form
              onSubmit={handleSubmit(handleSaveTemplate)}
              data-testid={`productions-and-exports-details-form`}
            >
              <ArrTextField
                id="templateName"
                dataTestId="template-name-text-input"
                state={errors.templateName ? 'error' : 'enabled'}
                label={t(
                  'productionsAndExports.saveAsTemplateModal.templateName'
                )}
                placeholder={t(
                  'productionsAndExports.saveAsTemplateModal.templateNamePlaceholder'
                )}
                {...register('templateName')}
                supportText={errors.templateName?.message || ''}
              />
            </form>
          </Box>
        </ArrModal>
      )}
      {createSetToast.show && (
        <ArrToastNotification
          message={createSetToast.message}
          status={[createSetToast.type]}
          setErrorIconAsAlert={createSetToast.type !== ToastType.Success}
          onClose={() =>
            setCreateSetToast({
              show: false,
              type: ToastType.Success,
              message: '',
            })
          }
        />
      )}
      {deleteToast.show && (
        <ArrToastNotification
          message={deleteToast.message}
          status={[deleteToast.type]}
          setErrorIconAsAlert={deleteToast.type !== ToastType.Success}
          onClose={() =>
            setDeleteToast({
              show: false,
              type: ToastType.Success,
              message: '',
            })
          }
        />
      )}
      {templateToast.show && (
        <ArrToastNotification
          message={templateToast.message}
          status={[templateToast.type]}
          setErrorIconAsAlert={templateToast.type !== ToastType.Success}
          onClose={() =>
            setTemplateToast({
              show: false,
              type: ToastType.Success,
              message: '',
            })
          }
        />
      )}
      {cancelProductionToast.show && (
        <ArrToastNotification
          message={cancelProductionToast.message}
          status={[cancelProductionToast.type]}
          setErrorIconAsAlert={cancelProductionToast.type !== ToastType.Success}
          onClose={() =>
            setCancelProductionToast({
              show: false,
              type: ToastType.Success,
              message: '',
            })
          }
        />
      )}
      {updateSetToast.show && (
        <ArrToastNotification
          message={updateSetToast.message}
          status={[updateSetToast.type]}
          setErrorIconAsAlert={updateSetToast.type !== ToastType.Success}
          onClose={() =>
            setUpdateSetToast({
              show: false,
              type: ToastType.Success,
              message: '',
            })
          }
        />
      )}
      <ArrModal
        showModal={showConfirm}
        showClose={false}
        onClose={() => setShowConfirm(false)}
        title={t('cancelProduction.confirmation.title')}
        description={t('cancelProduction.confirmation.description')}
        dataTestId="cancelprod-modal"
        buttons={[
          {
            id: '1',
            label: t('common.close'),
            onClick: () => setShowConfirm(false),
            variant: 'secondary',
            dataTestId: 'cpm-close-btn',
          },
          {
            id: '2',
            label: t('common.confirm'),
            onClick: () => triggerCancel(),
            variant: 'primary',
            dataTestId: 'cpm-confirm-btn',
          },
        ]}
      />
    </Box>
  );
};

export default ProductionsAndExportsDetails;
